import { ClockPickerView } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { FormDateTimePicker } from "../../../../../components/fields";
import useDebounceCallback from "../../../../../hooks/useDebouncedCallback";
import { RentalLocationType } from "../../../../../types";
import { shouldDisableRentalDate, shouldDisableRentalTime } from "../../../../../utils";
import { isBusinessContinuityMode, rangeDateValidity, stepState } from "../../../recoil";
import { IFormValues, InsertBookingStep } from "../../../types";
import { GlobalContext } from "../../../../../../../util/state/GlobalContextProvider";
import { IRangeDate } from "../../../../../api/services";
import { convertUtcToTzIsoDayJs } from "../../../../../../../util/timezoneConversion";

const FormPickupDate = () => {

    const { t } = useTranslation('booking');

    const step = useRecoilValue(stepState);
    const rangeDate = useRecoilValue<IRangeDate | undefined>(rangeDateValidity);
    const businessContinuityMode = useRecoilValue(isBusinessContinuityMode);

    const { trigger, getValues } = useFormContext<IFormValues>();
    const { globalData } = useContext(GlobalContext);

    const bookingDateTime = useWatch<IFormValues, "basicDetails.bookingDateTime">({
        name: 'basicDetails.bookingDateTime'
    });


    const handleChange = useDebounceCallback(() => {
        trigger('basicDetails.pickupDate');
        if (getValues('basicDetails.dropoffDate')) {
            trigger('basicDetails.dropoffDate');
        }
    }, 300, []);
    const shouldDisableDate = useCallback((date: dayjs.Dayjs) => {
        const dmsAssociatedOperator = globalData.user.mobilityStores.map(
            ((elem: any) => {
                return elem.sapId;
            })
        );
        return shouldDisableRentalDate(date, getValues('basicDetails.pickupLocation'), RentalLocationType.Pickup, dmsAssociatedOperator, rangeDate);
    }, [rangeDate]);
    const shouldDisableTime = useCallback((time: number, clockTime: ClockPickerView) => {
        const dmsAssociatedOperator = globalData.user.mobilityStores.map(
            ((elem: any) => {
                return elem.sapId;
            })
        );
        return shouldDisableRentalTime(time, clockTime, RentalLocationType.Pickup, getValues('basicDetails.pickupLocation'), getValues('basicDetails.pickupDate'), dmsAssociatedOperator);
    }, []);

    const pickupLocation = getValues('basicDetails.pickupLocation');
    const timezone = pickupLocation?.zoneId ?? 'Europe/Rome';
    const currentDate = useMemo(() => convertUtcToTzIsoDayJs(dayjs().toISOString(), timezone) ?? undefined, [timezone]);

    return (
        <FormDateTimePicker<IFormValues>
            name="basicDetails.pickupDate"
            label={t('label.pickupDate')}
            onChange={handleChange}
            minDateTime={businessContinuityMode ? bookingDateTime?.add(1, "minute") || undefined : currentDate}
            maxDateTime={businessContinuityMode ? currentDate : undefined}
            shouldDisableDate={shouldDisableDate}
            shouldDisableTime={shouldDisableTime}
            disabled={step === InsertBookingStep.CUSTOMER_DETAILS}
        />
    );
};

export default FormPickupDate;
